import type { CatalogLibrary, InstalledEfLibrary } from '@/types';
import { api } from './api';

// Emission factor libraries bound to a scenario — village's scenario-keyed
// surface. A scenario binds libraries that the ledger resolves by precedence.
//
//   - `getScenarioEfLibraries` lists a named scenario's libraries (the scenario
//     detail page's Emission factors section).
//   - `getEfLibraries` lists the inventory's *default* scenario's libraries (the
//     inventory-level audit read).
//   - attach / re-pin / re-prioritise are one upsert (`installEfLibrary`); it
//     returns the updated list. `uninstallEfLibrary` detaches.
//   - `getEfLibraryCatalog` feeds the "add library" picker (the attachable
//     libraries; the picker filters out the already-attached ones).
const efLibrariesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getEfLibraries: builder.query<InstalledEfLibrary[], number>({
      query: (inventoryId) => `inventories/${inventoryId}/ef-libraries`,
      providesTags: ['EfLibraries'],
    }),
    getScenarioEfLibraries: builder.query<InstalledEfLibrary[], number>({
      query: (scenarioId) => `scenarios/${scenarioId}/ef-libraries`,
      providesTags: ['EfLibraries'],
    }),
    getEfLibraryCatalog: builder.query<CatalogLibrary[], number>({
      query: (inventoryId) => `inventories/${inventoryId}/ef-libraries/catalog`,
      providesTags: ['EfLibraryCatalog'],
    }),
    installEfLibrary: builder.mutation<
      InstalledEfLibrary[],
      {
        scenarioId: number;
        library: number;
        version: number | null;
        precedence: number;
      }
    >({
      query: ({ scenarioId, library, version, precedence }) => ({
        url: `scenarios/${scenarioId}/ef-libraries`,
        method: 'POST',
        body: { library, version, precedence },
      }),
      invalidatesTags: ['EfLibraries'],
    }),
    uninstallEfLibrary: builder.mutation<
      void,
      { scenarioId: number; library: number }
    >({
      query: ({ scenarioId, library }) => ({
        url: `scenarios/${scenarioId}/ef-libraries/${library}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EfLibraries'],
    }),
  }),
});

export const {
  useGetEfLibrariesQuery,
  useGetScenarioEfLibrariesQuery,
  useGetEfLibraryCatalogQuery,
  useInstallEfLibraryMutation,
  useUninstallEfLibraryMutation,
} = efLibrariesApi;
